import { useMemo, useState } from 'react'

import { Container, Divider } from '@doist/marketist'

import { Interpolate } from 'components'
import { ContainerSidebar } from 'components/container-sidebar/container-sidebar'
import { useTranslation } from 'next-i18next'
import { formatPrice, PriceTable, SupportedCurrencies } from 'services/pricing/price-table'
import { setSessionCurrency } from 'services/pricing/session'

import { BillingCycles, PlanControls } from '../Controls/Controls'
import { FeatureList, Plan } from '../Plan/Plan'

import css from './Plans.module.css'

type PlansProps = {
    currency: SupportedCurrencies
    priceTable: PriceTable
}

export function Plans({ currency, priceTable }: PlansProps) {
    const { t } = useTranslation('pricing')
    const [userCurrency, setUserCurrency] = useState(currency)
    const [billingCycle, setBillingCycle] = useState<BillingCycles>('yearly')

    const priceTag = useMemo(
        () => priceTable[userCurrency as SupportedCurrencies],
        [userCurrency, priceTable],
    )

    const onChangeHandler = (value: SupportedCurrencies) => {
        setUserCurrency(value)
        setSessionCurrency(value)
    }

    const onBillingCycleChangeHandler = (value: BillingCycles) => {
        setBillingCycle(value)
    }

    return (
        <div className={css.planContainer}>
            <ContainerSidebar tag="section">
                <ContainerSidebar.Aside className={css.desktopPlanControls}>
                    <PlanControls className={css.asideControls}>
                        <PlanControls.SignUpButton divider />
                        <PlanControls.BillingCycle
                            billingCycle={billingCycle}
                            onBillingCycleChange={onBillingCycleChangeHandler}
                        />
                        <PlanControls.Currency currency={userCurrency} onChange={onChangeHandler} />
                    </PlanControls>
                </ContainerSidebar.Aside>
                <ContainerSidebar.Main className={css.plansContainer}>
                    <Plan title={t('freePlan')} currencySymbol={priceTag.symbol} cost="0">
                        <FeatureList>
                            <Interpolate
                                t={t}
                                key="featureHistoryAccess"
                                i18nKey="featureHistoryAccess"
                            />
                            <Interpolate
                                t={t}
                                key="featureIntegrations"
                                i18nKey="featureIntegrations"
                            />
                            <Interpolate
                                t={t}
                                key="featureFileStorage"
                                i18nKey="featureFileStorage"
                            />
                            <Interpolate
                                t={t}
                                key="featureStandardSupport"
                                i18nKey="featureStandardSupport"
                            />
                            <Interpolate
                                t={t}
                                key="featureMemberAccounts"
                                i18nKey="featureMemberAccounts"
                            />
                            <Interpolate
                                t={t}
                                key="featureExternalGuest500"
                                i18nKey="featureExternalGuest500"
                            />
                        </FeatureList>
                    </Plan>
                    <Plan
                        title={t('unlimited')}
                        currencySymbol={priceTag.symbol}
                        cost={formatPrice(
                            billingCycle === 'yearly'
                                ? priceTag.unlimited.yearly
                                : priceTag.unlimited.monthly,
                            priceTag.locale,
                            userCurrency,
                        )}
                    >
                        <FeatureList>
                            <Interpolate
                                t={t}
                                key="featureFullHistory"
                                i18nKey="featureFullHistory"
                            />
                            <Interpolate
                                t={t}
                                key="featureUnlimitedIntegrations"
                                i18nKey="featureUnlimitedIntegrations"
                            />
                            <Interpolate
                                t={t}
                                key="featureUnlimitedStorage"
                                i18nKey="featureUnlimitedStorage"
                            />
                            <Interpolate
                                t={t}
                                key="featurePrioritySupport"
                                i18nKey="featurePrioritySupport"
                            />
                            <Interpolate
                                t={t}
                                key="featureUnlimitedAccounts"
                                i18nKey="featureUnlimitedAccounts"
                            />
                            <Interpolate
                                t={t}
                                key="featureExternalGuests"
                                i18nKey="featureExternalGuests"
                            />
                        </FeatureList>
                    </Plan>
                </ContainerSidebar.Main>
            </ContainerSidebar>
            <Container className={css.mobilePlanControls}>
                <PlanControls>
                    <PlanControls.BillingCycle
                        billingCycle={billingCycle}
                        onBillingCycleChange={onBillingCycleChangeHandler}
                    />
                    <PlanControls.Currency currency={userCurrency} onChange={onChangeHandler} />
                    <PlanControls.SignUpButton />
                </PlanControls>
            </Container>
            <Container className={css.dividerContainer} padded={false}>
                <Divider />
            </Container>
        </div>
    )
}
